import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Y from "yjs";
import { applyJsonDiffToYjs, getYjsEventsHandler, jsonToYType } from "vyjs";
import { data } from "react-router";
import { OneDriveProvider } from "@/utils/oneDriveCustomProvider";
import { FormDefinition } from "@/types/form";
import { useUsername } from "./useProfile";
import { useIsAuthenticated } from "@azure/msal-react";
import { useIsConnected } from "./useIsConnected";

export type YTypeToJson<YType> = YType extends Y.Array<infer Value>
  ? Array<YTypeToJson<Value>>
  : YType extends Y.Map<infer MapValue>
  ? { [key: string]: YTypeToJson<MapValue> }
  : string;

export type JsonToYType<T> = T extends Array<infer Value>
  ? Y.Array<JsonToYType<Value>>
  : T extends { [key: string]: infer MapValue }
  ? Y.Map<JsonToYType<MapValue>>
  : Y.Text;

export function useMultilayerDoc(): Y.Doc | undefined {
  const userName = useUsername();
  const isAuthenticated = useIsAuthenticated();
  const isConnected = useIsConnected();
  const [doc, setDoc] = useState<Y.Doc | undefined>(undefined);
  const [oneDriveProvider, setOneDriveProvider] = useState<OneDriveProvider | undefined>(undefined);

  useEffect(() => {
    if (userName === undefined) {
      return;
    }

    const doc = new Y.Doc();
    const oneDriveProviderPromise = OneDriveProvider.getOneDriveProvider(userName!, doc);
    oneDriveProviderPromise.then((provider) => {
      setOneDriveProvider(provider);
      setDoc(doc);
    });

    return () => {
      setDoc(undefined);
      oneDriveProviderPromise.then((provider) => {
        provider.destroy();
      });
      setOneDriveProvider(undefined);
    }
  }, [userName]);

  useEffect(() => {
    if (oneDriveProvider !== undefined && isAuthenticated && isConnected) {
      oneDriveProvider.tryInitOneDriveWebSocket();

      return () => oneDriveProvider.disconnectWebsocket();
    }
  }, [isAuthenticated, isConnected, oneDriveProvider]);

  return doc;
}

export const DocContext = createContext<Y.Doc | undefined>(undefined);

export function useYDoc<T>(
  pathEntries: PathEntry[],
  initialData: T
): [T, (arg: T | ((state: T) => T)) => void] {
  const fullDoc = useContext(DocContext)!;

  const vyjsNode = useMemo<JsonToYType<T> | undefined>(() => {
    type ReduceState = {
      node: Y.Map<unknown> | Y.Array<unknown> | undefined;
      initialValue: unknown;
    };

    return pathEntries.reduce<ReduceState>(
      ({ node, initialValue }, pathEntry) => {
        if (node === undefined || pathEntry.type === "empty") {
          return { node: undefined, initialValue: undefined };
        }
        if (pathEntry.type === "map") {
          const key = pathEntry.value;
          const mapNode = node as Y.Map<unknown>;
          if (!mapNode.has(key)) {
            (node as Y.Map<unknown>).set(key, jsonToYType(initialValue));
          }
          return {
            node: mapNode.get(key) as Y.Map<unknown>,
            initialValue,
          };
        }

        if (pathEntry.type === "array") {
          const key = pathEntry.value;
          const arrayNode = node as Y.Array<unknown>;

          for (const item of arrayNode.toArray()) {
            const mapItem = item as Y.Map<unknown>;
            const idEntry = mapItem.get("id") as Y.Text;
            if (idEntry.toJSON() === key) {
              return { node: mapItem, initialValue };
            }
          }

          throw data("Record Not Found", { status: 404 });
        }

        throw new Error("Invalid path entry type");
      },
      { node: fullDoc.getMap("root"), initialValue: initialData }
    ).node as JsonToYType<T> | undefined;
  }, [pathEntries, fullDoc, initialData]);

  initialData = useMemo(() => vyjsNode?.toJSON() as T, [vyjsNode]);

  const [currentState, setCurrentState] = useState<T>(initialData);
  const currentStateRef = useRef<T>(currentState);
  currentStateRef.current = currentState;

  useEffect(() => {
    const eventHandler = getYjsEventsHandler(initialData, setCurrentState);
    vyjsNode?.observeDeep(eventHandler);

    return () => vyjsNode?.unobserveDeep(eventHandler);
  }, [initialData, vyjsNode]);

  const setState = useCallback(
    (arg: T | ((state: T) => T)) => {
      if (typeof arg === "function") {
        const func = arg as (state: T) => T;

        applyJsonDiffToYjs(
          currentStateRef.current,
          func(currentStateRef.current),
          vyjsNode
        );
      } else {
        applyJsonDiffToYjs(currentStateRef.current, arg, vyjsNode);
      }
    },
    [vyjsNode]
  );

  return [currentState, setState];
}

export function useYDocReducer<S, A>(
  pathEntries: PathEntry[],
  reducer: (state: S, action: A) => S,
  initialData: S
): [S, React.Dispatch<A>] {
  const [state, setState] = useYDoc(pathEntries, initialData);

  const dispatch: React.Dispatch<A> = useCallback((action) => {
    setState((state) => reducer(state, action));
  }, [reducer, setState]);

  return [state, dispatch];
}

type EmptyPathEntry = {
  type: "empty";
};

type FormResponsesPathEntry = {
  type: "map";
  value: "formResponses";
};

type ViewPathEntry = {
  type: "map";
  value: "views";
};

type FormDefinitionPathEntry = {
  type: "map";
  value: "formDefinition";
};

type MapPathEntry = {
  type: "map";
  value: string;
};

type ArrayPathEntry = {
  type: "array";
  value: string;
};

export type PathEntry =
  | ArrayPathEntry
  | EmptyPathEntry
  | MapPathEntry
  | FormResponsesPathEntry
  | ViewPathEntry
  | FormDefinitionPathEntry;

export const viewsPathEntry: ViewPathEntry = { type: "map", value: "views" };

export const formResponsesPathEntry: FormResponsesPathEntry = {
  type: "map",
  value: "formResponses",
};

export const formDefinitionPathEntry: FormDefinitionPathEntry = { type: "map", value: "formDefinition" };

export const defaultForm: FormDefinition = { titleFieldId: undefined, sections: [] };
